import {Component, Input, OnInit, ViewChild, ViewEncapsulation} from "@angular/core";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {MatSort, Sort} from "@angular/material/sort";
import {AnalyticsService} from "../../../services/analytics/analytics.service";
import {AlertService} from "../../../services/alert/alert.service";
import {TopItemsSoldReportModel} from "../../../model/analytics/top-items-sold-report.model";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: "app-top-items-sold",
  templateUrl: "top-items-sold.component.html",
  styleUrls: ["./top-items-sold.component.scss"],
  encapsulation: ViewEncapsulation.None
})
export class TopItemsSoldComponent implements OnInit {

  @Input() businessId: string;

  public loadingPage = false;
  DEFAULT_PAGE_SIZE = 10;

  topProduct: string;
  topItemsList: {productName: string, numberOfSales: number, revenue: number}[];
  tempSortedArray: any[] = null;

  sortedData: any[];

  paginationOptions = {
    length: this.DEFAULT_PAGE_SIZE,
    pageSize: this.DEFAULT_PAGE_SIZE,
    pageIndex: 0,
    pageSizeOptions: [5, 10, 25, 100]
  };

  startDateModel: Date;
  endDateModel: Date;

  lastSortOptions = {
    active: null,
    direction: null
  };

  USE_MOCK_DATA = false;

  constructor(
    private analyticsService: AnalyticsService,
    private alertService: AlertService,
    private translate: TranslateService) {

  }

  ngOnInit() {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    this.startDateModel = oneWeekAgo;
    this.endDateModel = new Date();

    if (this.USE_MOCK_DATA) {
      this.topItemsList = this.getMockTopItemsList();
      this.paginationOptions.length = this.topItemsList.length;
      this.sortedData = this.topItemsList.slice(0, this.paginationOptions.pageSize);
      this.applyFilter();
    } else {
      this.fetchTopItemsSoldReport(false);
    }
  }

  public fetchTopItemsSoldReport(displaySuccessMessage?: boolean) {
    this.loadingPage = true;
    this.analyticsService.getTopItemsSold(this.businessId,
      this.analyticsService.getDateAsString(this.startDateModel), this.analyticsService.getDateAsString(this.endDateModel)).subscribe(
      next => {
        this.loadingPage = false;
        if (next && next._body) {
          const topItemsSoldReport: TopItemsSoldReportModel = JSON.parse(next._body);
          this.topItemsList = [];
          for (const property in topItemsSoldReport.topItemsSold) {
            this.topItemsList.push({
              productName: property,
              numberOfSales: topItemsSoldReport.topItemsSold[property].timesSold,
              revenue: topItemsSoldReport.topItemsSold[property].totalRevenue
            });
            if (!this.topProduct) {
              this.topProduct = property;
            }
          }
          this.paginationOptions.length = this.topItemsList.length;
          this.sortedData = this.topItemsList.slice(0, this.paginationOptions.pageSize);
          this.applyFilter();
          if (displaySuccessMessage) {
            this.alertService.toastSuccess(this.translate.instant("page.dashboard.analytics.topItemsSold.message.loadSuccess"));
          }
        } else {
          this.alertService.toastError(this.translate.instant("page.dashboard.analytics.topItemsSold.message.loadFail"));
          console.error(this.translate.instant("page.dashboard.analytics.topItemsSold.message.loadFail")
            + " Error: " + JSON.stringify(next));
        }
      }, error => {
        this.loadingPage = false;
        this.alertService.toastError(this.translate.instant("page.dashboard.analytics.topItemsSold.message.loadError"));
        console.error(this.translate.instant("page.dashboard.analytics.topItemsSold.message.loadFail")
          + " Error: " + JSON.stringify(error));
      }
    );
  }

  public dateFilterChanged() {
    this.fetchTopItemsSoldReport(true);
  }

  sortData(sort: Sort) {
    const data = this.tempSortedArray ? this.tempSortedArray.slice() : this.topItemsList.slice();

    this.lastSortOptions.active = sort.active;
    this.lastSortOptions.direction = sort.direction;

    if (!sort.active || sort.direction === '') {
      this.sortedData = data.slice(
        this.paginationOptions.pageIndex * this.paginationOptions.pageSize,
        (this.paginationOptions.pageIndex * this.paginationOptions.pageSize) + this.paginationOptions.pageSize);
      return;
    }

    this.tempSortedArray = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'productName': return compare(a.productName, b.productName, isAsc);
        case 'numberOfSales': return compare(a.numberOfSales, b.numberOfSales, isAsc);
        case 'revenue': return compare(a.revenue, b.revenue, isAsc);
        default: return 0;
      }
    });
    this.sortedData = this.tempSortedArray.slice(
      this.paginationOptions.pageIndex * this.paginationOptions.pageSize,
      (this.paginationOptions.pageIndex * this.paginationOptions.pageSize) + this.paginationOptions.pageSize)
  }

  paginatorChange(event?: PageEvent) {
    console.log(' event = ' + JSON.stringify(event));
    if (this.tempSortedArray) {
      this.sortedData = this.tempSortedArray.slice(event.pageIndex * event.pageSize, (event.pageIndex * event.pageSize) + event.pageSize);
    } else {
      this.sortedData = this.topItemsList.slice(event.pageIndex * event.pageSize,
        (event.pageIndex * event.pageSize) + event.pageSize);
    }
    this.paginationOptions.pageSize = event.pageSize;
    this.paginationOptions.pageIndex = event.pageIndex;
  }

  applyFilter() {
    const startDate = this.startDateModel;
    const endDate = this.endDateModel;
    if (!startDate && !endDate) {
      this.tempSortedArray = null;
      this.sortData({active: this.lastSortOptions.active, direction: this.lastSortOptions.direction});
      return;
    }

    const tempElementsArray = this.topItemsList.slice();
    this.tempSortedArray = [];
    for (let i = 0; i < tempElementsArray.length; i++) {
      let isAllowed = true;
      if (isAllowed) {
        this.tempSortedArray.push(tempElementsArray[i]);
      }
    }
    this.paginationOptions.length = this.tempSortedArray.length;
    this.paginationOptions.pageIndex = 0;
    this.sortedData = this.tempSortedArray.slice(
      this.paginationOptions.pageIndex * this.paginationOptions.pageSize,
      (this.paginationOptions.pageIndex * this.paginationOptions.pageSize) + this.paginationOptions.pageSize);
    this.sortData({active: this.lastSortOptions.active, direction: this.lastSortOptions.direction});
  }

  private getMockTopItemsList(): {productName: string, numberOfSales: number, revenue: number}[] {
    this.topProduct = 'Lasagna spinacche';
    return [
      {productName: 'Lasagna con spinacche', numberOfSales: 33, revenue: 1000},
      {productName: 'Pizza quatro carni', numberOfSales: 29, revenue: 900},
      {productName: 'Lioncello', numberOfSales: 28, revenue: 1000},
      {productName: 'File o fish', numberOfSales: 22, revenue: 800},
      {productName: 'Cola', numberOfSales: 20, revenue: 232},
      {productName: 'Fried chicken', numberOfSales: 18, revenue: 111},
      {productName: 'Bugs fried', numberOfSales: 15, revenue: 3232},
      {productName: 'Tomattose', numberOfSales: 15, revenue: 111},
      {productName: 'Pizza Siciliana', numberOfSales: 10, revenue: 2},
      {productName: 'Whiskey', numberOfSales: 7, revenue: 123},
      {productName: 'Fanta 0.25l', numberOfSales: 7, revenue: 321},
      {productName: 'Chhescake', numberOfSales: 5, revenue: 321},
      {productName: 'Cozonac', numberOfSales: 4, revenue: 321},
      {productName: 'Yogurt', numberOfSales: 3, revenue: 321},
      {productName: 'Moet 0.75', numberOfSales: 1, revenue: 1000}
    ];
  }
}

function compare(a: number | string, b: number | string, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
